'use client';

import React, { isValidElement } from 'react';
import { type PropsWithChildren, type RefAttributes, createContext, useContext } from 'react';
import {
    Tag as AriaTag,
    TagGroup as AriaTagGroup,
    type TagGroupProps as AriaTagGroupProps,
    TagList as AriaTagList,
    type TagProps as AriaTagProps,
} from 'react-aria-components';
import { Avatar, type AvatarProps } from '@milanote/roma/components/base/avatar/avatar';
import { Dot } from '@milanote/roma/components/base/tags/base-components/dot-icon';
import { cx } from '@milanote/roma/utils/cx';
import { isReactComponent } from '@milanote/roma/utils/is-react-component';
import { TagCloseX } from './base-components/tag-close-x';
import { isLightBackgroundColor } from '../../../utils/color';

const SEMANTIC_NAME_GROUP = 'RomaTagGroup';
const SEMANTIC_NAME_BASE = 'RomaTagBase';
const SEMANTIC_NAME = 'RomaTag';

export interface TagItem {
    id: string;
    label: string;
    variant?: 'normal' | 'user' | 'success' | 'warning' | 'error';
    color?: string; // Hex or CSS color string. Overrides variant colors.
    count?: number;
    avatar?: AvatarProps;
    dot?: boolean;
    dotClassName?: string;
    isDisabled?: boolean;
    onClose?: (id: string) => void;
}

const TagGroupContext = createContext<{
    size: 'xs' | 'sm' | 'md' | 'lg';
}>({
    size: 'sm',
});

export interface TagGroupProps extends AriaTagGroupProps, RefAttributes<HTMLDivElement> {
    label: string;
    size?: 'xs' | 'sm' | 'md' | 'lg';
}

export const TagGroup = ({ label, size = 'sm', selectionMode = 'none', children, ...otherProps }: TagGroupProps) => {
    return (
        <TagGroupContext.Provider value={{ size }}>
            <AriaTagGroup
                aria-label={label}
                selectionMode={selectionMode}
                {...otherProps}
                className={cx(SEMANTIC_NAME_GROUP, otherProps.className as string)}
            >
                {children}
            </AriaTagGroup>
        </TagGroupContext.Provider>
    );
};

TagGroup.displayName = SEMANTIC_NAME_GROUP;

export const TagList = AriaTagList;

const styles = {
    sizes: {
        xs: {
            root: {
                base: 'px-1.25 py-0 text-3xs font-medium rounded-xxs',
                withAvatar: 'pl-0.75',
                withDot: 'pl-0.75',
                withCount: 'pr-0.75',
                withClose: 'pr-0.75',
            },
            content: 'gap-0.75',
            count: 'h-3 px-0.75 text-3xs font-medium',
            icon: 'size-3',
        },
        sm: {
            root: {
                base: 'px-1.5 py-0 text-xs font-medium rounded-xxs',
                withAvatar: 'pl-1',
                withDot: 'pl-1',
                withCount: 'pr-1',
                withClose: 'pr-1',
            },
            content: 'gap-1',
            count: 'h-3.5 px-1 text-xs font-medium',
            icon: 'size-3.5',
        },
        md: {
            root: {
                base: 'px-2.25 py-0.5 text-md font-medium rounded-xs',
                withAvatar: 'pl-1.25',
                withDot: 'pl-1.25',
                withCount: 'pr-0.75',
                withClose: 'pr-1',
            },
            content: 'gap-1.25',
            count: 'h-4 px-1.25 text-xs font-medium',
            icon: '',
        },
        lg: {
            root: {
                base: 'px-2.5 py-1 text-md font-medium rounded-xs',
                withAvatar: 'pl-1.75',
                withDot: 'pl-1.75',
                withCount: 'pr-1',
                withClose: 'pr-1',
            },
            content: 'gap-1.5',
            count: 'h-5 px-1.5 text-sm font-medium',
            icon: '',
        },
    },
    variant: {
        normal: {
            base: 'bg-primary text-secondary ring-1 ring-primary ring-inset',
            count: 'bg-tertiary text-center',
            closeX: 'text-fg-quaternary',
        },
        user: {
            base: 'bg-bg-user-primary text-text-user-primary',
            count: '',
            closeX: '',
        },
        success: {
            base: 'bg-utility-success-100 text-utility-success-700',
            count: '',
            closeX: '',
        },
        warning: {
            base: 'bg-utility-warning-200 text-utility-warning-700',
            count: '',
            closeX: '',
        },
        error: {
            base: 'bg-utility-error-200 text-utility-error-700',
            count: '',
            closeX: '',
        },
        colored_light: {
            base: 'bg-(--tag-bg-color) text-black/80',
            count: 'text-black/70',
            closeX: 'text-black/50',
        },
        colored_dark: {
            base: 'bg-(--tag-bg-color) text-white/80',
            count: 'text-white/70',
            closeX: 'text-white/50',
        },
    },
};

interface TagProps extends AriaTagProps, RefAttributes<object>, Omit<TagItem, 'label' | 'id'> {
    tagBaseClassName?: string;
}

export type TagBaseProps = {
    size?: 'xs' | 'sm' | 'md' | 'lg';
    leadingIcon?: React.FC<{ className?: string }> | React.ReactNode;
    avatar?: AvatarProps;
    dot?: boolean;
    dotClassName?: string;
    variant?: 'normal' | 'user' | 'success' | 'warning' | 'error';
    color?: string; // Hex or CSS color string. Overrides variant colors.
    count?: number;
    isDisabled?: boolean;
    isSelected?: boolean;
    allowsRemoving?: boolean;
    onClose?: () => void;
    className?: string;
    children?: React.ReactNode;
};

const getLeadingContent = ({
    size = 'sm',
    leadingIcon,
    avatar,
    dot,
    dotClassName,
}: TagBaseProps): [React.ReactNode, string] => {
    if (leadingIcon && isValidElement(leadingIcon)) return [leadingIcon, styles.sizes[size].root.withDot];

    if (leadingIcon && isReactComponent(leadingIcon)) {
        const IconComponent = leadingIcon as React.FC<{ className?: string }>;
        return [<IconComponent className={styles.sizes[size].icon} />, styles.sizes[size].root.withDot];
    }

    if (avatar) {
        return [
            <Avatar size="xxs" {...avatar} alt="Avatar" className={styles.sizes[size].icon} />,
            styles.sizes[size].root.withAvatar,
        ];
    }

    if (dot) {
        return [
            <Dot className={cx('text-fg-success-secondary', dotClassName)} size="sm" />,
            styles.sizes[size].root.withDot,
        ];
    }

    return [null, ''];
};

export const TagBase = ({
    size = 'sm',
    leadingIcon,
    avatar,
    dot,
    dotClassName,
    variant = 'normal',
    color,
    count,
    isDisabled,
    isSelected,
    allowsRemoving,
    onClose,
    className,
    children,
}: TagBaseProps) => {
    const [leadingContent, leadingContentClasses] = getLeadingContent({
        size,
        leadingIcon,
        avatar,
        dot,
        dotClassName,
    });

    const isColored = Boolean(color);
    const isColoredLight = isColored ? isLightBackgroundColor(color ?? '') : false;
    const actualVariant = isColored ? (isColoredLight ? 'colored_light' : 'colored_dark') : variant;
    const cssVariables = isColored
        ? ({
              '--tag-bg-color': color,
          } as React.CSSProperties)
        : undefined;

    return (
        <span
            className={cx(
                SEMANTIC_NAME_BASE,
                'flex cursor-default items-center gap-0.75 bg-primary text-secondary',
                'transition duration-100 ease-linear',
                styles.sizes[size].root.base,
                styles.variant[actualVariant].base,
                // Classes for leading content
                leadingContentClasses,
                // With X button
                (onClose || allowsRemoving) && styles.sizes[size].root.withClose,
                // With count
                typeof count === 'number' && styles.sizes[size].root.withCount,
                // Disabled
                isDisabled && 'cursor-not-allowed',
                isSelected && 'ring-2 ring-primary_focused',

                className,
            )}
            style={cssVariables}
        >
            <span className={cx('inline-flex min-w-0 items-center gap-1', styles.sizes[size].content)}>
                {leadingContent}

                <span className="truncate">{children}</span>

                {typeof count === 'number' && (
                    <span
                        className={cx(
                            'flex items-center justify-center rounded-xxs',
                            styles.variant[actualVariant].count,
                            styles.sizes[size].count,
                        )}
                    >
                        {count}
                    </span>
                )}
            </span>

            {(onClose || allowsRemoving) && (
                <TagCloseX size={size} onPress={onClose} className={styles.variant[actualVariant].closeX} />
            )}
        </span>
    );
};

export const Tag = ({
    id,
    avatar,
    dot,
    variant = 'normal',
    color,
    dotClassName,
    isDisabled,
    count,
    className,
    children,
    onClose,
    tagBaseClassName,
}: PropsWithChildren<TagProps>) => {
    const context = useContext(TagGroupContext);
    const size = context.size;

    return (
        <AriaTag
            id={id}
            isDisabled={isDisabled}
            textValue={typeof children === 'string' ? children : undefined}
            className={(state) =>
                cx(
                    'focus:outline-hidden focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-focus-ring',
                    typeof className === 'function' ? className(state) : className,
                )
            }
        >
            {({ allowsRemoving, isSelected }) => (
                <TagBase
                    size={size}
                    avatar={avatar}
                    dot={dot}
                    dotClassName={dotClassName}
                    variant={variant}
                    color={color}
                    count={count}
                    isDisabled={isDisabled}
                    isSelected={isSelected}
                    allowsRemoving={allowsRemoving}
                    onClose={id && onClose ? () => onClose(id.toString()) : undefined}
                    className={tagBaseClassName}
                >
                    {children}
                </TagBase>
            )}
        </AriaTag>
    );
};

TagBase.displayName = SEMANTIC_NAME_BASE;
Tag.displayName = SEMANTIC_NAME;
